import React, { useState, useEffect, useRef } from 'react';

/**
 * Utility to detect if the current device/viewport is mobile.
 * Checks both viewport media query (<= 768px) and userAgent for mobile devices.
 */
export const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;
    const isMobileWidth = window.matchMedia
        ? window.matchMedia('(max-width: 768px)').matches
        : window.innerWidth <= 768;
    const isMobileUA = typeof navigator !== 'undefined'
        && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
    return Boolean(isMobileWidth || isMobileUA);
};

/**
 * Isolated Iframe Banner for Adsterra Skyscraper ads.
 * Rendering via srcDoc isolates `window.atOptions` and script execution,
 * preventing collisions between multiple Adsterra ads and protecting the main DOM.
 */
export const IframeBanner = ({ title, width, height, adKey }) => {
    const iframeRef = useRef(null);

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <base target="_blank">
  <style>
    html, body {
      margin: 0;
      padding: 0;
      overflow: hidden;
      width: ${width}px;
      height: ${height}px;
      background: transparent;
    }
  </style>
</head>
<body>
  <script type="text/javascript">
    atOptions = {
      'key' : '${adKey}',
      'format' : 'iframe',
      'height' : ${height},
      'width' : ${width},
      'params' : {}
    };
  </script>
  <script type="text/javascript" src="https://bauval.org/22/${adKey}"></script>
</body>
</html>`;

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
            if (doc && (!doc.body || doc.body.innerHTML === '')) {
                doc.open();
                doc.write(htmlContent);
                doc.close();
            }
        } catch (e) {
            // Ignore potential cross-origin restrictions in sandboxed environments
        }
    }, [htmlContent]);

    return (
        <iframe
            ref={iframeRef}
            title={title}
            srcDoc={htmlContent}
            width={width}
            height={height}
            style={{ border: 'none', overflow: 'hidden', display: 'block', width: `${width}px`, height: `${height}px` }}
            scrolling="no"
            frameBorder="0"
        />
    );
};

/**
 * Top Container CPM Banner for Russia (Session 017 format).
 */
export const RussianAdBanner = () => {
    const bannerRef = useRef(null);

    useEffect(() => {
        const wrapper = bannerRef.current;
        if (!wrapper) return;

        // Prevent duplicate script injection
        if (wrapper.querySelector('script[src*="4abde7b7f557bb4626b26cc59ada6f9f"]')) {
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://pl31851626.profitableratecpmnetwork.com/4abde7b7f557bb4626b26cc59ada6f9f/invoke.js';
        script.async = true;
        script.setAttribute('async', 'async');
        script.setAttribute('data-cfasync', 'false');

        const container = wrapper.querySelector('#container-4abde7b7f557bb4626b26cc59ada6f9f');
        if (container) {
            wrapper.insertBefore(script, container);
        } else {
            wrapper.appendChild(script);
        }
    }, []);

    return (
        <div
            ref={bannerRef}
            className="ru-ad-wrapper mb-4"
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100px'
            }}
        >
            <div id="container-4abde7b7f557bb4626b26cc59ada6f9f"></div>
        </div>
    );
};

/**
 * Wide Skyscraper Side Rails for Russian visitors (Google-like side rails).
 * Renders on widescreen desktop (min-width: 1460px) pinned on left (160x600) and right (160x300) margins.
 * Includes Google-style header with "Реклама" badge and close button.
 */
export const RussianSideRails = () => {
    const [closedLeft, setClosedLeft] = useState(false);
    const [closedRight, setClosedRight] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    return (
        <div className="ru-side-rails-container" aria-label="Russian Side Rail Ads">
            {!closedLeft && (
                <aside
                    className="ru-side-rail ru-side-rail-left"
                    aria-label="Advertisement Left"
                    data-testid="ru-side-rail-left"
                >
                    <div className="ru-side-rail-header">
                        <span className="ru-side-rail-label"></span>
                        <button
                            type="button"
                            className="ru-side-rail-close"
                            onClick={() => setClosedLeft(true)}
                            aria-label="Закрыть рекламу"
                            title="Закрыть"
                        >
                            &times;
                        </button>
                    </div>
                    <div className="ru-side-rail-content">
                        <IframeBanner
                            title="ru-ad-skyscraper-left"
                            width={160}
                            height={600}
                            adKey="467026ac661fc3df7aad157a2779766a"
                        />
                    </div>
                </aside>
            )}

            {!closedRight && (
                <aside
                    className="ru-side-rail ru-side-rail-right"
                    aria-label="Advertisement Right"
                    data-testid="ru-side-rail-right"
                >
                    <div className="ru-side-rail-header">
                        <span className="ru-side-rail-label"></span>
                        <button
                            type="button"
                            className="ru-side-rail-close"
                            onClick={() => setClosedRight(true)}
                            aria-label="Закрыть рекламу"
                            title="Закрыть"
                        >
                            &times;
                        </button>
                    </div>
                    <div className="ru-side-rail-content">
                        <IframeBanner
                            title="ru-ad-skyscraper-right"
                            width={160}
                            height={300}
                            adKey="b8e776ca4ec250a082f2fe516b1bf008"
                        />
                    </div>
                </aside>
            )}
        </div>
    );
};

/**
 * Adsterra Social Bar component for Russian visitors on mobile responsive viewport.
 * Injects https://bauval.org/14/d5c9863ec27fe1d83df836607dce15db only on mobile.
 */
export const RussianSocialBar = () => {
    const [isMobile, setIsMobile] = useState(isMobileDevice);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const checkMobile = () => {
            setIsMobile(isMobileDevice());
        };

        window.addEventListener('resize', checkMobile);
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!isMobile) return;

        const src = 'https://bauval.org/14/d5c9863ec27fe1d83df836607dce15db';
        if (document.querySelector('script[src*="d5c9863ec27fe1d83df836607dce15db"]')) {
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.setAttribute('data-cfasync', 'false');

        document.body.appendChild(script);

        // For test environments (jsdom)
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
            setTimeout(() => {
                if (script.onload) script.onload();
            }, 10);
        }
    }, [isMobile]);

    return null;
};
